import { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toIntlLocale } from '../../language/intlLocale';
import moment from 'moment';
import useFetch from '../../api/useFetch';
import { API_PUT, DBRequest, useConfig } from '../../api/api';
import { tokens } from '../../global/theme/tokens';
import ExoDialog from '../../components/exo/ExoDialog';
import ExoPaymentRequest from '../../components/exo/ExoPaymentRequest.tsx';
import { getInitials } from '../../components/exo/ExoAvatar';

const INTERVAL_MONTHS = { monthly: 1, annually: 12, biannually: 24 };
const INTERVAL_LABELS = {
  monthly: 'Monthly',
  annually: 'Annually',
  biannually: 'Biannually',
};
const WINDOWS = ['session', 'daily', 'weekly'];
const USAGE_LABELS = {
  session: '{{percent}} % of the session budget used',
  daily: '{{percent}} % of the daily budget used',
  weekly: '{{percent}} % of the weekly budget used',
};

// a tier is paid when it is not flagged free and actually costs something
// per month (the licensing server may list a free tier with zero prices)
function monthlyPrice(tier) {
  const price = Number(tier?.prices?.monthly?.price);
  return Number.isFinite(price) ? price : null;
}

function isPaidTier(tier) {
  return !tier?.isFree && monthlyPrice(tier) > 0;
}

// subscription prices of the tiers (what the tier costs per month), never
// anything about the AI budget itself
function formatPrice(price, currency = 'EUR', language = 'de') {
  const value = Number(price);
  if (!Number.isFinite(value)) return '';
  try {
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(value);
  } catch (e) {
    return `${value.toFixed(2)} ${currency || ''}`.trim();
  }
}

/**
 * Settings tab: which user is on which AI tier. Tiers and assignments come
 * from the licensing server through the backend. The budget itself is only
 * ever shown relatively (share of a window used); caps, costs and token
 * counts are not disclosed.
 */
export const AiUsageSettings = ({ onFeedback = () => {} }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const catalog = useFetch('ai-tiers');
  const assignments = useFetch('ai-tiers/assignments');

  // paid upgrade waiting for the price confirmation: { row, tier, interval }
  const [pending, setPending] = useState(null);
  const [savingUserId, setSavingUserId] = useState(null);
  const [rowErrors, setRowErrors] = useState({});

  const tierData = catalog.apiData?.data?.tiers;
  const tiers = useMemo(() => tierData ?? [], [tierData]);
  const currency = catalog.apiData?.data?.currency ?? 'EUR';
  const assignmentData = assignments.apiData?.data?.assignments;
  const rows = useMemo(() => assignmentData ?? [], [assignmentData]);
  const language = toIntlLocale(i18n.language);

  const tierByKey = useMemo(
    () => Object.fromEntries(tiers.map((tier) => [tier.key, tier])),
    [tiers]
  );

  const seats = useMemo(
    () =>
      rows.reduce((acc, row) => {
        const key = row.tier?.key;
        if (key) acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {}),
    [rows]
  );

  const monthlyTotal = tiers.reduce((sum, tier) => {
    if (!isPaidTier(tier)) return sum;
    return sum + monthlyPrice(tier) * (seats[tier.key] || 0);
  }, 0);

  function handleSelect(row, tierKey) {
    const tier = tierByKey[tierKey];
    if (!tier) return;
    // re-selecting the current tier only matters to cancel a scheduled downgrade
    if (tierKey === row.tier?.key && !row.nextTier) return;

    const current = row.tier ? tierByKey[row.tier.key] : null;
    const isUpgrade = !current || tier.sortOrder > current.sortOrder;

    // a paid upgrade is confirmed with its price and the terms; downgrades,
    // free tiers and cancelling a downgrade go straight through
    if (isPaidTier(tier) && isUpgrade) {
      setPending({ row, tier, interval: 'monthly' });
      return;
    }
    assign(row, tier, null, false);
  }

  function assign(row, tier, interval, termsAccepted) {
    setPending(null);
    setRowErrors((prev) => ({ ...prev, [row.user.id]: null }));

    DBRequest({
      config,
      path: `users/${row.user.id}/ai-tier`,
      method: API_PUT({
        tierKey: tier.key,
        paymentInterval: interval,
        termsAccepted,
      }),
      disableRetry: true,
      onLoading: (loading) => setSavingUserId(loading ? row.user.id : null),
      onResponse: (res) => {
        const updated = res.data?.assignment;
        if (!updated) return;
        assignments.setApiData((prev) => ({
          ...prev,
          data: {
            ...prev.data,
            assignments: prev.data.assignments.map((item) =>
              item.user.id === updated.user.id ? updated : item
            ),
          },
        }));
        onFeedback(t('AI tier updated'), 'success');
      },
      onFeedback: (message, type, error) => {
        if (type !== 'error') return;
        // the licensing server's validation errors arrive as { errors: { field: [..] } }
        const details = error?.errors
          ? Object.values(error.errors).flat().join(' ')
          : null;
        setRowErrors((prev) => ({
          ...prev,
          [row.user.id]: details || message,
        }));
      },
    });
  }

  function paymentItems({ row, tier, interval }) {
    const months = INTERVAL_MONTHS[interval];
    const endDate = moment()
      .add(months, 'M')
      .format(config?.general?.dateOutputFormat || 'DD.MM.YYYY');
    return [
      {
        name: `${tier.label} (${row.user.firstName} ${row.user.lastName}) - ${t(
          'Valid Until'
        )} ${endDate}`,
        description: tier.description ? (
          <Typography variant="h6" fontSize="12px">
            {tier.description}
          </Typography>
        ) : null,
        amount: 1,
        taxRate: 19,
        price: Number(tier.prices[interval]?.price || 0) * months,
      },
    ];
  }

  const loading = catalog.isLoading || assignments.isLoading;
  const failed = catalog.serverError || assignments.serverError;

  return (
    <Box className="w-full flex flex-col gap-6">
      {loading && <LinearProgress />}

      {failed && !loading && (
        <Alert severity="error">
          {t('The AI tiers could not be loaded from the licensing server.')}
        </Alert>
      )}

      {tiers.length > 0 && (
        <Box
          className="grid gap-3"
          sx={{ gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}
        >
          {tiers.map((tier) => (
            <TierCard
              key={tier.key}
              tier={tier}
              seats={seats[tier.key] || 0}
              currency={currency}
              language={language}
              colors={colors}
            />
          ))}
        </Box>
      )}

      {rows.length > 0 && (
        <>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {t(
              'Upgrades apply immediately. Downgrades take effect when the current subscription period ends; selecting the current tier again cancels a scheduled downgrade.'
            )}
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('Employee')}</TableCell>
                <TableCell>{t('Tier')}</TableCell>
                <TableCell>{t('Status')}</TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  {t('Current usage')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <AssignmentRow
                  key={row.user.id}
                  row={row}
                  tiers={tiers}
                  currency={currency}
                  language={language}
                  colors={colors}
                  saving={savingUserId === row.user.id}
                  error={rowErrors[row.user.id]}
                  dateFormat={config?.general?.dateOutputFormat}
                  onSelect={(tierKey) => handleSelect(row, tierKey)}
                />
              ))}
            </TableBody>
          </Table>

          <Box className="flex flex-col items-end gap-1">
            <Typography variant="h5" fontWeight={600}>
              {t('Monthly total')}:{' '}
              {formatPrice(monthlyTotal, currency, language)}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              {t('Seats per paid tier × monthly price, net')}
            </Typography>
          </Box>
        </>
      )}

      <ExoDialog
        limitWidth
        open={Boolean(pending)}
        onClose={() => setPending(null)}
      >
        {pending && (
          <Box className="flex flex-col gap-4 p-4">
            <Typography variant="h4" fontWeight={600}>
              {pending.tier.label} · {pending.row.user.firstName}{' '}
              {pending.row.user.lastName}
            </Typography>
            <Box className="flex items-center gap-3">
              <Typography variant="body2">{t('Payment interval')}</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={pending.interval}
                onChange={(e, value) =>
                  value && setPending({ ...pending, interval: value })
                }
              >
                {Object.keys(INTERVAL_MONTHS).map((interval) => (
                  <ToggleButton key={interval} value={interval}>
                    {t(INTERVAL_LABELS[interval])}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <ExoPaymentRequest
              items={paymentItems(pending)}
              onSubmit={() =>
                assign(pending.row, pending.tier, pending.interval, true)
              }
              onCancel={() => setPending(null)}
            />
          </Box>
        )}
      </ExoDialog>
    </Box>
  );
};

const TierCard = ({ tier, seats, currency, language, colors }) => {
  const { t } = useTranslation();

  return (
    <Box
      className="flex flex-col gap-2 p-4 rounded-xl"
      sx={{
        border: `1px solid ${colors.selected}`,
        bgcolor: colors.card,
      }}
    >
      <Box className="flex justify-between items-center gap-2">
        <Typography variant="h5" fontWeight={600}>
          {tier.label}
        </Typography>
        <Chip size="small" label={`${seats} ${t('Users')}`} />
      </Box>
      {tier.description && (
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          {tier.description}
        </Typography>
      )}
      <Typography variant="h6" fontWeight={600}>
        {isPaidTier(tier)
          ? `${formatPrice(monthlyPrice(tier), currency, language)} ${t('per month')}`
          : t('Free of charge')}
      </Typography>
    </Box>
  );
};

// the window with the largest share used, or null without any per-user cap
function bindingUsage(usage) {
  if (!usage) return null;
  let binding = null;
  WINDOWS.forEach((window) => {
    const percent = usage[window]?.percent;
    if (percent === null || percent === undefined) return;
    if (!binding || percent > binding.percent) binding = { window, percent };
  });
  return binding;
}

const AssignmentRow = ({
  row,
  tiers,
  currency,
  language,
  colors,
  saving,
  error,
  dateFormat,
  onSelect,
}) => {
  const { t } = useTranslation();
  const name = `${row.user.firstName} ${row.user.lastName}`;
  const usage = bindingUsage(row.usage);
  const barColor =
    usage?.percent >= 90
      ? colors.error
      : usage?.percent >= 75
        ? colors.warning
        : colors.info;
  const endDate = row.subscription?.endDate
    ? moment(row.subscription.endDate).format(dateFormat || 'DD.MM.YYYY')
    : null;

  return (
    <TableRow>
      <TableCell>
        <Box className="flex items-center gap-2">
          <Avatar
            alt={name}
            src={row.user.picture?.thumbnailUrl}
            sx={{ width: 32, height: 32, fontSize: 13 }}
          >
            {getInitials(name)}
          </Avatar>
          <Box className="flex flex-col">
            <Typography variant="body2" fontWeight={600}>
              {name}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              {row.user.email}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Box className="flex flex-col gap-1">
          <Select
            size="small"
            value={row.tier?.key ?? ''}
            disabled={saving}
            onChange={(e) => onSelect(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            {tiers.map((tier) => (
              <MenuItem key={tier.key} value={tier.key}>
                <Box className="flex flex-col">
                  <Typography variant="body2">
                    {tier.label}
                    {isPaidTier(tier) &&
                      ` · ${formatPrice(monthlyPrice(tier), currency, language)} ${t('per month')}`}
                  </Typography>
                  {tier.description && (
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.6, whiteSpace: 'normal' }}
                    >
                      {tier.description}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
          {error && (
            <Typography variant="caption" sx={{ color: colors.error }}>
              {error}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Box className="flex flex-col gap-1">
          {!row.synced && (
            <Tooltip title={t('Not synced with the licensing server yet')}>
              <Chip
                size="small"
                variant="outlined"
                label={t('Not synced with the licensing server yet')}
              />
            </Tooltip>
          )}
          {endDate && (
            <Typography variant="caption">
              {t('Active until {{date}}', { date: endDate })}
            </Typography>
          )}
          {row.nextTier && (
            <Typography variant="caption" sx={{ color: colors.warning }}>
              {t('Then {{tier}}', { tier: row.nextTier.label })}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell>
        {usage ? (
          <Box className="flex flex-col gap-1">
            <LinearProgress
              variant="determinate"
              value={usage.percent}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: colors.bgInput,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: barColor,
                  borderRadius: 3,
                },
              }}
            />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {t(USAGE_LABELS[usage.window], { percent: usage.percent })}
            </Typography>
          </Box>
        ) : (
          <Typography variant="caption" sx={{ opacity: 0.6 }}>
            {t('No per-user cap')}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

export default AiUsageSettings;
